// A big file in parts: each part retried on its own, at most three at once, read from the file only while it is on its
// way. What is uploaded so far stays in localStorage, so the same file picks up where it stopped after a reload.
import { ApiError } from "../api.ts";
import type { UploadProgress } from "./upload.ts";

/** Above this a file goes in parts; the part size itself comes from the server. */
export const MULTIPART_ABOVE_BYTES = 64 * 1024 * 1024;
export const MAX_PARALLEL_PARTS = 3;
/** Tries per part, the first included. */
export const PART_ATTEMPTS = 5;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 30_000;
const POLL_MS = 3000;
/** How long we wait for the check of a big file before we leave it and say so. */
const VERIFY_WAIT_MS = 60 * 60 * 1000;
/** Calls to complete one upload in one go: two tabs can each find the other just took it. */
export const COMPLETE_TRIES = 5;

export interface UploadedPart {
  partNumber: number;
  etag: string;
}

export type MultipartState = "uploading" | "verifying" | "done" | "failed" | "aborted";

export interface MultipartStatus {
  upload_id: string;
  state: MultipartState;
  error?: string | null;
  part_bytes: number;
  part_count: number;
}

export interface MultipartStart {
  exists: boolean;
  upload_id?: string;
  part_bytes?: number;
  part_count?: number;
}

export interface MultipartDeps {
  start(): Promise<MultipartStart>;
  status(uploadId: string): Promise<MultipartStatus>;
  signPart(uploadId: string, partNumber: number): Promise<{ url: string; headers: Record<string, string> }>;
  /** Send the part; answers its ETag. `onBytes` hears how much of this try went out. */
  putPart(
    url: string,
    headers: Record<string, string>,
    part: Blob,
    onBytes: (loaded: number) => void,
    signal: AbortSignal,
  ): Promise<string>;
  complete(
    uploadId: string,
    parts: UploadedPart[],
  ): Promise<{ state: MultipartState; error?: string | null }>;
  abort(uploadId: string): Promise<unknown>;
  /** Default: localStorage, when the browser lets us. */
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
  /** In tests. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

/** What survives a reload. */
export interface ResumeEntry {
  sha256: string;
  size: number;
  uploadId: string;
  parts: UploadedPart[];
}

const entryKey = (ws: string, sha256: string, size: number) => `filmsquare.upload.${ws}.${sha256}.${size}`;

function defaultStorage(): MultipartDeps["storage"] {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readEntry(deps: MultipartDeps, key: string, sha256: string, size: number): ResumeEntry | null {
  try {
    const raw = deps.storage?.getItem(key);
    if (!raw) return null;
    const e = JSON.parse(raw) as ResumeEntry;
    const fits =
      e.sha256 === sha256 &&
      e.size === size &&
      typeof e.uploadId === "string" &&
      Array.isArray(e.parts) &&
      e.parts.every((p) => Number.isInteger(p.partNumber) && typeof p.etag === "string");
    return fits ? e : null;
  } catch {
    return null;
  }
}

function writeEntry(deps: MultipartDeps, key: string, entry: ResumeEntry): void {
  try {
    deps.storage?.setItem(key, JSON.stringify(entry));
  } catch {
    // no room or no permission: the upload goes on, it just cannot resume
  }
}

function dropEntry(deps: MultipartDeps, key: string): void {
  try {
    deps.storage?.removeItem(key);
  } catch {
    // nothing to do
  }
}

function waitFor(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const done = () => {
      signal?.removeEventListener("abort", stop);
      resolve();
    };
    const t = setTimeout(done, ms);
    const stop = () => {
      clearTimeout(t);
      reject(signal?.reason);
    };
    signal?.addEventListener("abort", stop, { once: true });
  });
}

/** Up to `cap`, half of it random, so three parts failing together do not come back together. */
export const backoffMs = (attempt: number, random = Math.random): number => {
  const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** (attempt - 1));
  return ceiling / 2 + random() * (ceiling / 2);
};

/** Worth another try: the network, the server's bad moment, an expired URL (each try signs a new one). */
export function retryable(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  return (
    e.status === 0 ||
    e.status === 403 ||
    e.status === 408 ||
    e.status === 425 ||
    e.status === 429 ||
    e.status >= 500
  );
}

/** The upload does not exist any more on the store (expired, aborted elsewhere): start over. */
const isGone = (e: unknown) =>
  e instanceof ApiError &&
  (e.status === 404 || e.code === "upload_gone" || e.code === "bad_parts" || e.code === "upload_not_open");

export interface UploadInPartsInput {
  ws: string;
  file: Blob;
  sha256: string;
  signal?: AbortSignal;
  onProgress?: (p: UploadProgress) => void;
}

/** Upload `file` in parts, resuming what an earlier try of the same bytes left. Resolves when the blob is stored. */
export async function uploadInParts(deps0: MultipartDeps, input: UploadInPartsInput): Promise<void> {
  const deps: MultipartDeps = {
    ...deps0,
    storage: deps0.storage === undefined ? defaultStorage() : deps0.storage,
  };
  const { file, sha256, signal } = input;
  const key = entryKey(input.ws, sha256, file.size);
  const sleep = deps.sleep ?? waitFor;

  let current: string | undefined;

  const putAll = async (uploadId: string, partBytes: number, count: number, have: UploadedPart[]) => {
    const parts = new Map(have.filter((p) => p.partNumber <= count).map((p) => [p.partNumber, p.etag]));
    const save = () =>
      writeEntry(deps, key, {
        sha256,
        size: file.size,
        uploadId,
        parts: [...parts].map(([partNumber, etag]) => ({ partNumber, etag })),
      });
    save();
    const sizeOf = (n: number) => (n < count ? partBytes : file.size - (count - 1) * partBytes);
    let settled = 0;
    for (const n of parts.keys()) settled += sizeOf(n);
    const inFlight = new Map<number, number>();
    let shown = 0;
    const report = () => {
      let bytes = settled;
      for (const b of inFlight.values()) bytes += b;
      // a retried part starts again from zero: the bar does not walk back
      shown = Math.max(shown, Math.min(1, bytes / file.size));
      input.onProgress?.({ phase: "put", fraction: shown });
    };
    report();

    const todo: number[] = [];
    for (let n = 1; n <= count; n++) if (!parts.has(n)) todo.push(n);
    const stop = new AbortController();
    const onCancel = () => stop.abort(signal?.reason);
    if (signal?.aborted) onCancel();
    else signal?.addEventListener("abort", onCancel, { once: true });

    const sendPart = async (n: number) => {
      for (let attempt = 1; ; attempt++) {
        stop.signal.throwIfAborted();
        try {
          const signed = await deps.signPart(uploadId, n);
          const at = (n - 1) * partBytes;
          // A slice is a reference to the file: its bytes are read as the request sends them.
          const etag = await deps.putPart(
            signed.url,
            signed.headers,
            file.slice(at, at + sizeOf(n)),
            (loaded) => {
              inFlight.set(n, loaded);
              report();
            },
            stop.signal,
          );
          inFlight.delete(n);
          settled += sizeOf(n);
          parts.set(n, etag);
          save();
          report();
          return;
        } catch (e) {
          inFlight.delete(n);
          stop.signal.throwIfAborted();
          if (attempt >= PART_ATTEMPTS || !retryable(e)) throw e;
          await sleep(backoffMs(attempt), stop.signal);
        }
      }
    };
    const runner = async () => {
      for (let n = todo.shift(); n !== undefined; n = todo.shift()) await sendPart(n);
    };
    try {
      await Promise.all(Array.from({ length: MAX_PARALLEL_PARTS }, runner));
    } catch (e) {
      stop.abort(e);
      throw e;
    } finally {
      signal?.removeEventListener("abort", onCancel);
    }
    return [...parts].map(([partNumber, etag]) => ({ partNumber, etag }));
  };

  /**
   * Until the server's check is over. A lost connection here is not the end: ask again. False: it is open again, because the
   * call that was completing it let go or died, and no job will end this wait. Then it is up to whoever is asking.
   */
  const waitChecked = async (uploadId: string): Promise<boolean> => {
    input.onProgress?.({ phase: "verify", fraction: 1 });
    const until = Date.now() + VERIFY_WAIT_MS;
    while (Date.now() < until) {
      await sleep(POLL_MS, signal);
      const s = await deps.status(uploadId).catch((e) => {
        if (retryable(e)) return null;
        throw e;
      });
      if (s?.state === "done") return true;
      if (s?.state === "uploading") return false;
      if (s?.state === "failed") throw notStored(s.error);
      if (s?.state === "aborted") throw new ApiError(409, "upload_gone", "This upload was stopped.");
    }
    throw new ApiError(
      0,
      "upload_failed",
      "The check of this file is taking long. Try again later: it resumes.",
    );
  };

  /** The parts in place, then complete, then wait; again if it opens up again, a few times and with a pause. */
  const finish = async (
    uploadId: string,
    partBytes: number,
    count: number,
    have: UploadedPart[],
    checking: boolean,
  ) => {
    let parts = have;
    let wait = checking;
    for (let tries = 0; ; ) {
      if (wait && (await waitChecked(uploadId))) return;
      if (tries === COMPLETE_TRIES)
        throw new ApiError(0, "upload_failed", "This file could not be finished now. Try again: it resumes.");
      if (tries > 0) await sleep(backoffMs(tries), signal);
      current = uploadId;
      parts = await putAll(uploadId, partBytes, count, parts);
      tries++;
      let done: { state: MultipartState; error?: string | null };
      try {
        done = await deps.complete(uploadId, parts);
      } catch (e) {
        if ((tries === 1 && !checking) || !retryable(e)) throw e;
        wait = false;
        continue;
      }
      if (done.state === "done") return;
      if (done.state === "failed") throw notStored(done.error);
      if (done.state === "aborted") throw new ApiError(409, "upload_gone", "This upload was stopped.");
      wait = done.state === "verifying";
    }
  };

  const once = async () => {
    const entry = readEntry(deps, key, sha256, file.size);
    const known = entry
      ? await deps.status(entry.uploadId).catch((e) => (isGone(e) ? null : Promise.reject(e)))
      : null;
    if (known?.state === "done") return;
    if (entry && known?.state === "verifying")
      return finish(entry.uploadId, known.part_bytes, known.part_count, entry.parts, true);
    let uploadId: string;
    let partBytes: number;
    let count: number;
    let have: UploadedPart[] = [];
    if (entry && known?.state === "uploading") {
      uploadId = entry.uploadId;
      partBytes = known.part_bytes;
      count = known.part_count;
      have = entry.parts;
    } else {
      dropEntry(deps, key);
      const started = await deps.start();
      if (started.exists) return;
      if (!started.upload_id || !started.part_bytes || !started.part_count)
        throw new ApiError(500, "upload_ticket", "the server sent no upload");
      uploadId = started.upload_id;
      partBytes = started.part_bytes;
      count = started.part_count;
    }
    await finish(uploadId, partBytes, count, have, false);
  };

  try {
    for (let startedOver = false; ; startedOver = true) {
      try {
        await once();
        break;
      } catch (e) {
        if (signal?.aborted || startedOver || !isGone(e)) throw e;
        dropEntry(deps, key);
      }
    }
    dropEntry(deps, key);
  } catch (e) {
    if (signal?.aborted) {
      if (current) await deps.abort(current).catch(() => undefined);
      dropEntry(deps, key);
    } else if (e instanceof ApiError && e.code === "sha256_mismatch") {
      // Those parts will never add up to this hash: resuming them would fail again.
      dropEntry(deps, key);
    }
    throw e;
  }
}

function notStored(code: string | null | undefined): ApiError {
  return code === "sha256_mismatch"
    ? new ApiError(400, "sha256_mismatch", "The file changed while it was uploading. Upload it again.")
    : new ApiError(400, code ?? "upload_failed", "This file could not be stored. Upload it again.");
}
